import _cloneDeep from 'lodash-es/cloneDeep';

import { ISG_FOLDER_EXTERNAL_ID } from '@iconik-io/core/src/constants/isg';
import type { AssetsAssetSchemaTypedWithId } from '@iconik-io/iconik-api/src/services/assets';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
} from '@iconik-io/iconik-api/src/types/object_types';
import { selectUserRoles } from '@iconik-io/shared-lib/src/redux/auth/selectors';
import { subtitlesFormats } from '@iconik-io/shared-lib/src/utils/constants/files';
import {
  UPLOADER_STATUS_WAITING,
  type UploaderState,
} from '@iconik-io/uploader/src/state';

import selectMergedPermissions from '../../components/permissions_manager/selectors';
import { UPLOADS_PANEL } from '../../constants/data';
import {
  NEW_SUBTITLE_MODAL,
  UPLOADER_ASSET_UPLOAD,
  UPLOADER_FILES_METADATA_MODAL,
} from '../../constants/modals';
import { SHARE_COLLECTION_PAGE_ROUTE } from '../../constants/routes';
import { getCurrentRoutePattern } from '../../router/current_route_params';
import type { AppDispatch, AppState, GetState } from '../../store/types';
import { endsWithAny } from '../../utils/asset_utils/asset_files';
import { isCollectionMappedToStorage } from '../../utils/collections';
import { getCollectionParents } from '../collections/thunks';
import { errorMessage } from '../messages/thunks';
import {
  getMetadataValues,
  putMetadataValues,
} from '../metadata_values/thunks';
import { openModal } from '../modals_manager/slice';
import { openPanel } from '../panels/slice';
import { uploaderAddCollections } from './collections_slice';
import { addNewAssetUploads, addPlaceholderUpload } from './engine/thunks';
import { getFileCollectionPath } from './helpers';
import { uploaderAddMetadata } from './metadata_slice';
import { selectUploads } from './selectors';
import type {
  MetadataUpdate,
  PickedFile,
  UploaderCollectionsAppState,
} from './types';
import { getUploadAssetId, isStagedUpload } from './upload_state';

export const getRequiredMetadataFiles =
  (stateIds: string[]) =>
  (_dispatch: AppDispatch, getState: GetState): UploaderState[] => {
    const state = getState();
    const requiredMetadataViewIds =
      state.settings.merged.required_metadata_views;
    if (!requiredMetadataViewIds?.length) {
      return [];
    }
    return stateIds
      .map((stateId) => state.uploaderStates[stateId])
      .filter(
        (upload): upload is UploaderState =>
          upload !== undefined &&
          isStagedUpload(upload) &&
          !requiredMetadataViewIds.includes(
            state.uploaderMetadata[upload.id]?.view,
          ),
      );
  };

export function addMetadata(
  stateId: string,
  metadata: MetadataUpdate,
  assetId?: string,
) {
  return (dispatch: AppDispatch) => {
    if (assetId) {
      dispatch(putMetadataValues(assetId, metadata.view, metadata.fields));
    }
    dispatch(uploaderAddMetadata({ stateId, metadata }));
  };
}

const getCollectionPath =
  (collectionId: string) => (dispatch: AppDispatch, getState: GetState) => {
    const entities = getState().entities;
    // this will set the path based on what we know at the time of
    // adding files to be uploaded to a collection
    // to make sure is correct the path will be pulling at the time of uploading from
    // handleCollectionsStructure
    const collection = entities.collections[collectionId];

    if (!collection) {
      dispatch(errorMessage({ text: `Parent collection not found` }));
      throw Error(`Parent collection ${collectionId} is not in the app state`);
    }

    const collectionIds = collection.parents ?? [];

    // filter out collections that are not in the app state, this is a normal case
    // during upload to a shared collections, when the shared collection is not a root one
    const collections = collectionIds
      .map((id) => entities.collections[id])
      .filter((item) => Boolean(item));

    // this is a condition for ISG and mapped collections
    if (collection.storage_id) {
      collections.shift();
    }

    return [...collections, collection].map(({ title }) => title).join('/');
  };

export const tryUploadToPlaceholder =
  (assetId: string, files: PickedFile[]) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    dispatch(openPanel(UPLOADS_PANEL));
    const file = files[0];

    const collectionId =
      getState().entities.assets[assetId].in_collections?.[0];

    if (collectionId) {
      await dispatch(getCollectionParents(collectionId));
    }

    const directoryPath = collectionId
      ? dispatch(getCollectionPath(collectionId))
      : undefined;

    const fileAlreadyExist = selectUploads(getState()).some(
      (upload) => getUploadAssetId(upload) === assetId,
    );
    const permissions = selectMergedPermissions(
      getState(),
      OBJECT_TYPE_ASSETS,
      assetId,
    );
    if (!permissions?.write) {
      dispatch(
        errorMessage({
          text: "You can't upload to this asset. Requires write permissions",
        }),
      );
      return;
    }
    if (fileAlreadyExist) {
      dispatch(
        errorMessage({
          text: 'You can only upload one file to placeholders',
        }),
      );
    } else {
      await Promise.allSettled(
        getState().settings.merged.required_metadata_views?.map((viewId) =>
          dispatch(getMetadataValues(assetId, { viewId })),
        ) ?? [],
      );
      const upload = await dispatch(
        addPlaceholderUpload(assetId, file, directoryPath),
      );
      dispatch(
        openModal({
          id: UPLOADER_FILES_METADATA_MODAL,
          contextItems: [upload],
          options: { onlyRequiredMetadata: true, reflectDirectories: false },
        }),
      );
    }
  };

export const tryUploadToAsset =
  (entity: AssetsAssetSchemaTypedWithId, files: PickedFile[]) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const permissions = selectMergedPermissions(
      getState(),
      OBJECT_TYPE_ASSETS,
      entity.id,
    );
    if (!permissions?.write) {
      dispatch(
        errorMessage({
          text: "You can't upload to this asset. Requires write permissions",
        }),
      );
      return;
    }
    const isSubtitles = files.every((file) =>
      endsWithAny(subtitlesFormats, file.name),
    );
    if (isSubtitles) {
      dispatch(
        openModal({
          id: NEW_SUBTITLE_MODAL,
          contextItems: [entity],
          options: { files },
        }),
      );
    } else {
      dispatch(
        openModal({
          id: UPLOADER_ASSET_UPLOAD,
          options: { files },
        }),
      );
    }
  };

const getSharedCollectionPermissions = (state: AppState) => {
  if (state.share.share.allow_upload) {
    return { read: true, write: true, delete: false };
  }
  return { read: true, write: false, delete: false };
};

export const tryUploadToCollection =
  (collectionId: string, files: PickedFile[]) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    const roles = selectUserRoles(getState());
    const permissions =
      getCurrentRoutePattern() === SHARE_COLLECTION_PAGE_ROUTE
        ? getSharedCollectionPermissions(getState())
        : selectMergedPermissions(
            getState(),
            OBJECT_TYPE_COLLECTIONS,
            collectionId,
          );

    const collection = getState().entities.collections[collectionId];

    const isRootMappedCollection =
      isCollectionMappedToStorage(collection) && collection?.is_root;

    if (
      !permissions?.write ||
      collection?.external_id === ISG_FOLDER_EXTERNAL_ID ||
      (isRootMappedCollection && !roles.web_can_top_level_upload)
    ) {
      dispatch(
        errorMessage({
          text: "You can't upload to this collection. Requires write permissions",
        }),
      );
      return;
    }

    dispatch(openPanel(UPLOADS_PANEL));

    const path = dispatch(getCollectionPath(collectionId));
    const uploads = await dispatch(addNewAssetUploads(files, collectionId));
    const fileCollections: UploaderCollectionsAppState = {};
    uploads.forEach((upload, index) => {
      fileCollections[upload.id] = [
        {
          path: getFileCollectionPath(
            path,
            { id: upload.id, fullpath: files[index].fullpath },
            getState().uploaderReducer.reflectDirectories,
          ),
          id: collectionId,
        },
      ];
    });
    dispatch(uploaderAddCollections(fileCollections));
  };

export const setInitialCollectionsPath =
  () => (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const uploads = selectUploads(state).filter(
      (upload) =>
        upload.options.baseCollectionId &&
        (isStagedUpload(upload) || upload.status === UPLOADER_STATUS_WAITING),
    );
    const collections = _cloneDeep(state.uploaderCollections);
    uploads.forEach((upload) => {
      if (collections[upload.id] && collections[upload.id].length) {
        collections[upload.id][0].path = getFileCollectionPath(
          dispatch(getCollectionPath(collections[upload.id][0].id)),
          { id: upload.id, fullpath: state.uploaderItems[upload.id]?.fullpath },
          state.uploaderReducer.reflectDirectories,
        );
      }
    });

    dispatch(uploaderAddCollections(collections));
  };
